// src/components/PrivacyPolicyModal.jsx
// ─────────────────────────────────────────────────────────────
//  Full-screen modal displaying the Sheepheard Privacy Policy.
//  Designed to open from PurposeModal's Disclaimer view before
//  authentication. Light-mode, scrollable, no external deps.
//  Fonts scaled ~2× for mobile readability.
//  Scroll bug fixed: flex column + minHeight:0 + overflow:scroll.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useRef } from 'react';
import { X, Shield, ChevronRight } from 'lucide-react';

// ── Effective date ─────────────────────────────────────────────
const EFFECTIVE_DATE = 'March 31, 2026';
const APP_EMAIL      = 'mr.reyn7@gmail.com'; // update as needed

// ── Typography helpers ─────────────────────────────────────────
const h2Style = {
  fontFamily: 'Georgia, serif',
  fontSize: '1.25rem',
  fontWeight: '700',
  color: '#14532d',
  margin: '0 0 10px',
  paddingBottom: '8px',
  borderBottom: '1px solid #d6e4d0',
};
const h3Style = {
  fontSize: '1rem',
  fontWeight: '800',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#4d7c0f',
  margin: '16px 0 6px',
};
const pStyle = {
  fontSize: '1rem',
  lineHeight: '1.75',
  color: '#3d5438',
  margin: '0 0 10px',
};
const liStyle = {
  fontSize: '1rem',
  lineHeight: '1.7',
  color: '#3d5438',
  marginBottom: '7px',
};
const highlightBox = (bg = '#f0faf4', border = '#c6e0c0') => ({
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: '10px',
  padding: '14px 16px',
  margin: '10px 0',
});

// ── Section component ──────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </div>
  );
}

// ── Main Modal ─────────────────────────────────────────────────
export default function PrivacyPolicyModal({ onClose }) {
  const overlayRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000, // above PurposeModal (9999)
        background: 'rgba(10, 30, 15, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        fontFamily: "'Manrope', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      {/* Sheet — full height, fixed flex column */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        height: '100%',
        background: '#f7fbf5',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 -8px 40px rgba(10,40,20,0.25)',
      }}>

        {/* Sticky header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px 14px',
          background: '#ffffff',
          borderBottom: '1px solid #d6e4d0',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '9px',
              background: 'linear-gradient(135deg,#4d7c0f,#166534)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Shield size={18} color="white" />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#14532d' }}>
                Privacy Policy
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#7aab6e' }}>
                Sheepheard · Effective {EFFECTIVE_DATE}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close privacy policy"
            style={{
              background: '#f0f0f0', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#666', flexShrink: 0,
            }}
          >
            <X size={17} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'scroll',
          WebkitOverflowScrolling: 'touch',
          padding: '22px 20px 48px',
        }}>

          {/* Preamble */}
          <div style={{ ...highlightBox('#edfaed', '#b6dbb0'), marginBottom: '24px' }}>
            <p style={{ ...pStyle, margin: 0, fontStyle: 'italic' }}>
              Your trust matters deeply to us. This policy explains clearly what data Sheepheard
              collects, how it is stored and used, and the choices you have. We do not sell your
              data — ever.
            </p>
          </div>

          {/* 1. Who We Are */}
          <Section title="1. Who We Are">
            <p style={pStyle}>
              Sheepheard ("the App", "we", "us", "our") is a Progressive Web Application (PWA)
              designed as a faith-centred discipleship companion. The App is operated by the
              Sheepheard development team and is available at{' '}
              <strong style={{ color: '#14532d' }}>disciplesheep.space</strong>.
            </p>
            <p style={pStyle}>
              Questions about this policy may be directed to:{' '}
              <strong style={{ color: '#14532d' }}>{APP_EMAIL}</strong>
            </p>
          </Section>

          {/* 2. Technology Stack & Data Flows */}
          <Section title="2. Technology Stack & How Your Data Flows">
            <p style={pStyle}>
              Understanding the services that power Sheepheard helps you understand where your
              data lives:
            </p>

            <h3 style={h3Style}>Firebase (Google LLC)</h3>
            <div style={highlightBox()}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={liStyle}>
                  <strong>Firebase Authentication</strong> — manages your email/password login.
                  Your credentials are hashed and stored securely by Google. We never see your
                  raw password.
                </li>
                <li style={liStyle}>
                  <strong>Cloud Firestore</strong> — stores your account data, discipleship tree,
                  journal entries, prayer records, people, and ministry notes in Google's
                  cloud database. Data is encrypted in transit (TLS) and at rest.
                </li>
                <li style={liStyle}>
                  Firebase services are governed by{' '}
                  <strong>Google's Privacy Policy</strong> and the{' '}
                  <strong>Firebase Data Processing Terms</strong>. Google may process data
                  on servers located outside your country of residence.
                </li>
              </ul>
            </div>

            <h3 style={h3Style}>Cloudflare</h3>
            <div style={highlightBox('#fffdf4', '#fde68a')}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={liStyle}>
                  The App is served through <strong>Cloudflare's CDN and WAF</strong> (Web
                  Application Firewall). Cloudflare may process your IP address and request
                  headers for DDoS protection, performance optimization, and bot mitigation.
                </li>
                <li style={liStyle}>
                  Cloudflare does not have access to your personal Sheepheard data stored in
                  Firestore. It only sees the network traffic (URL paths, timing, response codes).
                </li>
                <li style={liStyle}>
                  Cloudflare's data handling is governed by its own{' '}
                  <strong>Privacy Policy</strong> available at cloudflare.com.
                </li>
              </ul>
            </div>

            <h3 style={h3Style}>IndexedDB (On-Device Storage)</h3>
            <div style={highlightBox('#f4f0ff', '#d8cef5')}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={liStyle}>
                  Sheepheard uses your browser's <strong>IndexedDB</strong> to cache application
                  data locally. This enables offline access to your journal, Bible study notes,
                  and people records.
                </li>
                <li style={liStyle}>
                  IndexedDB data is stored <strong>only on your device</strong>. It is not
                  transmitted anywhere independently — it is a local mirror of your Firestore
                  data for offline use.
                </li>
                <li style={liStyle}>
                  Clearing your browser data or uninstalling the PWA will erase local IndexedDB
                  data. Your cloud data in Firestore is not affected.
                </li>
              </ul>
            </div>

            <h3 style={h3Style}>Service Worker (PWA Caching)</h3>
            <div style={highlightBox()}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={liStyle}>
                  A <strong>Service Worker</strong> caches static app files (HTML, JS, CSS,
                  icons) on your device so the app loads fast and works offline.
                </li>
                <li style={liStyle}>
                  The cache contains only app code — never your personal content or credentials.
                </li>
              </ul>
            </div>
          </Section>

          {/* 3. Data We Collect */}
          <Section title="3. Data We Collect">
            <h3 style={h3Style}>Account Data</h3>
            <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
              <li style={liStyle}>Email address (required for authentication)</li>
              <li style={liStyle}>Display name (optional, provided by you)</li>
              <li style={liStyle}>Hashed password (stored by Firebase Auth; we never see it)</li>
            </ul>

            <h3 style={h3Style}>Profile & Ministry Data</h3>
            <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
              <li style={liStyle}>Role, life stage, ministry name, and location (optional)</li>
              <li style={liStyle}>Discipleship tree structure and generation records</li>
            </ul>

            <h3 style={h3Style}>Spiritual & Ministry Records</h3>
            <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
              <li style={liStyle}>Journal entries, devotion notes, and sermon notes</li>
              <li style={liStyle}>Prayer journal entries and prayer requests</li>
              <li style={liStyle}>People records (names, contact notes, discipleship status)</li>
              <li style={liStyle}>Expense and stewardship records</li>
              <li style={liStyle}>Calendar events and milestones</li>
              <li style={liStyle}>Bible study guide responses and evangelism notes</li>
            </ul>

            <h3 style={h3Style}>Technical & Usage Data</h3>
            <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
              <li style={liStyle}>
                IP address and browser/device type — collected by Cloudflare for security
                purposes; not linked to your Sheepheard account by us.
              </li>
              <li style={liStyle}>
                App interaction timestamps (e.g., last login, entry creation dates) stored
                in Firestore for sync and display purposes.
              </li>
            </ul>

            <div style={{ ...highlightBox('#fff7ed', '#fed7aa'), marginTop: '8px' }}>
              <p style={{ ...pStyle, margin: 0, fontWeight: '600', color: '#92400e' }}>
                We do not collect: location GPS data, microphone or camera input,
                device contacts, financial payment data, or any biometric data.
              </p>
            </div>
          </Section>

          {/* 4. How We Use Your Data */}
          <Section title="4. How We Use Your Data">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {[
                'To authenticate you and secure your account.',
                'To store, sync, and display your personal discipleship records across devices.',
                'To enable offline access via local IndexedDB caching.',
                'To provide in-app features such as journaling, prayer tracking, and discipleship tree management.',
                'To send account-related emails (e.g., password reset) via Firebase Auth — no marketing emails without explicit consent.',
                'To protect the App from abuse, fraud, and security threats via Cloudflare.',
              ].map((item, i) => (
                <li key={i} style={{ ...liStyle, display: 'flex', gap: '10px', listStyle: 'none', marginBottom: '9px' }}>
                  <ChevronRight size={15} color="#4d7c0f" style={{ marginTop: '4px', flexShrink: 0 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* 5. Data Sharing */}
          <Section title="5. Data Sharing & Third Parties">
            <p style={pStyle}>
              We do not sell, rent, or trade your personal data. Data is shared only as follows:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={liStyle}>
                <strong>Firebase / Google LLC</strong> — as our database and authentication
                provider, they process data under their Data Processing Terms.
              </li>
              <li style={liStyle}>
                <strong>Cloudflare Inc.</strong> — as our CDN and security layer, they process
                network request metadata under their Data Processing Agreement.
              </li>
              <li style={liStyle}>
                <strong>Legal requirements</strong> — we may disclose data if required by law,
                court order, or to protect the rights and safety of users.
              </li>
            </ul>
            <p style={{ ...pStyle, marginTop: '12px' }}>
              No other third parties receive your personal information.
            </p>
          </Section>

          {/* 6. Data Retention */}
          <Section title="6. Data Retention">
            <p style={pStyle}>
              Your data is retained for as long as your account is active. If you delete your
              account, your Firestore data will be permanently deleted within{' '}
              <strong>30 days</strong>. Firebase Authentication records are deleted immediately
              upon account deletion request.
            </p>
            <p style={pStyle}>
              Local IndexedDB data is deleted automatically when you clear browser data or
              uninstall the PWA from your device.
            </p>
            <p style={pStyle}>
              Cloudflare access logs are retained per Cloudflare's standard retention policy
              (typically 30 days for free/pro plans).
            </p>
          </Section>

          {/* 7. Security */}
          <Section title="7. Security">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={liStyle}>All data in transit is encrypted via <strong>HTTPS / TLS 1.3</strong>.</li>
              <li style={liStyle}>Passwords are hashed by Firebase Authentication (bcrypt) — we cannot read them.</li>
              <li style={liStyle}>Firestore data is encrypted at rest by Google's infrastructure.</li>
              <li style={liStyle}>Cloudflare's WAF blocks malicious traffic before it reaches our servers.</li>
              <li style={liStyle}>Firebase Security Rules restrict data access to the authenticated owner only.</li>
            </ul>
            <div style={{ ...highlightBox('#fff7ed', '#fed7aa'), marginTop: '10px' }}>
              <p style={{ ...pStyle, margin: 0, color: '#92400e' }}>
                Despite these measures, no system is 100% secure. We encourage you to use a
                strong, unique password and to enable screen lock on your device.
              </p>
            </div>
          </Section>

          {/* 8. Changes */}
          <Section title="8. Changes to This Policy">
            <p style={pStyle}>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              effective date at the top of the document and, where appropriate, notify you via the
              email. Continued use of Sheepheard after changes constitutes acceptance of the updated
              policy.
            </p>
          </Section>

          {/* 9. Contact */}
          <Section title="9. Contact Us">
            <p style={pStyle}>
              For privacy concerns, data requests, or questions about this policy, please reach us at:
            </p>
            <div style={{ ...highlightBox(), textAlign: 'center' }}>
              <p style={{ margin: 0, fontWeight: '700', fontSize: '1.1rem', color: '#14532d' }}>
                {APP_EMAIL}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '0.9rem', color: '#7aab6e' }}>
                Sheepheard · A Discipleship Companion
              </p>
            </div>
          </Section>

          {/* Closing verse */}
          <div style={{ textAlign: 'center', padding: '8px 0 0' }}>
            <p style={{
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              fontSize: '1rem',
              color: '#4a6741',
              margin: '0 0 5px',
            }}>
              "He restores my soul." — Psalm 23:3
            </p>
            <p style={{ fontSize: '0.85rem', color: '#9aad94', margin: 0 }}>
              Sheepheard · Built for His Flock
            </p>
          </div>

        </div>

        {/* Sticky footer */}
        <div style={{
          padding: '14px 20px',
          background: '#ffffff',
          borderTop: '1px solid #d6e4d0',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #4d7c0f, #166534)',
              color: 'white',
              fontSize: '1.05rem',
              fontWeight: '800',
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
            }}
          >
            I Understand — Close
          </button>
        </div>
      </div>
    </div>
  );
}